import ClientFirearmActions from '@ClientActions/firearm.action'
import ClientMyAccountActions from '@ClientActions/my-account.action'
import BaseButton from '@Components/Form/BaseButton/BaseButton'
import BoxTitle from '@Components/Form/BoxTitle/BoxTitle'
import PageHeader from '@Components/Pages/PageHeader/PageHeader'
import {
    ICommonQuery,
    IPageHeaderActions,
    MCommonProduct,
} from '@Models/common.model'
import { Grid } from '@mui/material'
import ProductBuying from '@Pages/Client/my-account/ProductBuying'
import { APIServices } from '@Services/api.service'
import { ToastServices } from '@Services/toast.service'
import clsx from 'clsx'
import moment from 'moment'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'
import * as Redux from 'redux'
import './my-account.scss'
import Payments from './Payments'
import ReviewAndConfirmModal from './ReviewAndConfirmModal'
import Summary from './Summary'

// dummy data
const headerActions: IPageHeaderActions[] = [
    {
        name: 'Statement',
        value: '/my-account/statement',
    },
    {
        name: 'Receipt',
        value: '/my-account/receipt',
    },
]

const defaultValues = {
    cardNumber: '',
    nameOnCard: '',
    expiredDate: '',
    cvc: '',
}

// models section
interface IMyAccount {
    createInvoice: (payload: any) => Promise<any>
    getFirearms: (type: ICommonQuery) => void
    getAccessories: (type: ICommonQuery) => void
    getParts: (type: ICommonQuery) => void
    firearms: MCommonProduct[]
    accessories: MCommonProduct[]
    parts: MCommonProduct[]
}

function MyAccount(props: IMyAccount) {
    // core section
    const {
        getFirearms,
        getAccessories,
        getParts,
        createInvoice,
        firearms,
        accessories,
        parts,
    } = props
    const history = useHistory()
    const [showPayNowModal, setShowPayNowModal] = useState(false)
    const onHandleActions = (value: string) => {
        history.push(value)
    }
    const {
        handleSubmit,
        formState: { errors },
        control,
        reset,
    } = useForm({ defaultValues })

    // states section
    const [accountFirearms, setAccountFirearms] = useState<MCommonProduct[]>([])
    const [accountSummary, setAccountSummary] = useState<MCommonProduct[]>([])
    const [accountAccessories, setAccountAccessories] = useState<
        MCommonProduct[]
    >([])
    const [accountParts, setAccountParts] = useState<MCommonProduct[]>([])
    const [calculatedProducts, setCalculatedProducts] = useState<
        MCommonProduct[]
    >([])
    const [submitted, setSubmitted] = useState(false)
    const [disabled, setDisabled] = useState(false)
    const [tabValue, setTabValue] = useState(0)

    // hooks section
    useEffect(() => {
        const params = {
            status: 'BOUGHT',
        }
        getFirearms(params)
        getAccessories(params)
        getParts(params)
        return () => {
            APIServices.axiosCancelToken()
        }
    }, [])

    // hooks section
    /**
     * generate account summary when
     * accountFirearms or accountAccessories changed
     */
    useEffect(() => {
        if (
            accountFirearms?.length ||
            accountAccessories?.length ||
            accountParts?.length
        ) {
            const filteredAccountFirearms = accountFirearms?.length
                ? accountFirearms?.filter(
                      (item: MCommonProduct) => item?.checked
                  )
                : []
            const filteredAccountAccessories = accountAccessories?.length
                ? accountAccessories?.filter(
                      (item: MCommonProduct) => item?.checked
                  )
                : []
            const filteredAccountParts = accountParts?.length
                ? accountParts?.filter((item: MCommonProduct) => item?.checked)
                : []

            setAccountSummary([
                ...filteredAccountFirearms,
                ...filteredAccountAccessories,
                ...filteredAccountParts,
            ])
        } else {
            setAccountSummary([])
        }
        return () => {
            setAccountSummary([])
        }
    }, [accountFirearms, accountAccessories, accountParts])

    /**
     * handle form submit and create new invoice
     * @param formData inputs value
     * @returns
     */
    const onSubmit = (formData: any) => {
        const { cardNumber } = formData
        if (!calculatedProducts?.length) {
            ToastServices.error('Please select at least one item!')
            return setSubmitted(true)
        }
        setDisabled(true)
        setSubmitted(false)
        const items = calculatedProducts?.map((item: MCommonProduct) => {
            return {
                totalQty: 1,
                itemId: item?.id,
                referenceType: 'SUBSCRIPTION',
                referenceId: item?.itemSubscription?.id,
                unitPrice: +item?.itemSubscription?.totalAmount,
            }
        })

        const payment = {
            method: 'E-CASH',
            transactionMethod: tabValue === 0 ? 'VISA' : 'PAYPAL',
            cardNumber,
        }

        const body = {
            issuedAt: moment().toISOString(),
            type: 'SUBSCRIPTION',
            items,
            payment,
        }

        createInvoice(body)
            .then(() => {
                const params = {
                    status: 'BOUGHT',
                }
                getFirearms(params)
                getAccessories(params)
                setDisabled(false)
                reset(defaultValues)
            })
            .catch(() => setDisabled(false))
    }

    /**
     * fire button pay-now and submit form
     */
    const handlePayNow = () => {
        handleSubmit(onSubmit)()
    }

    // main
    return (
        <div className="my-account">
            <PageHeader
                title="My Account"
                actions={headerActions}
                getActions={(e: any) => onHandleActions(e)}
            />
            <Grid container spacing={3}>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <BoxTitle title="Firearms" />
                    <ProductBuying
                        productType="FIREARM"
                        productActions={setAccountFirearms}
                        data={firearms}
                        calculatedProducts={calculatedProducts}
                    />
                    <BoxTitle title="Accessories" />
                    <ProductBuying
                        productType="ACCESSORY"
                        productActions={setAccountAccessories}
                        data={accessories}
                        calculatedProducts={calculatedProducts}
                    />
                    <BoxTitle title="Part" />
                    <ProductBuying
                        productType="PART"
                        productActions={setAccountParts}
                        data={parts}
                        calculatedProducts={calculatedProducts}
                    />
                </Grid>
                <Grid item lg={6} md={6} sm={12} xs={12}>
                    <Summary
                        data={accountSummary}
                        className={clsx(
                            !accountSummary?.length &&
                                submitted &&
                                'border-danger'
                        )}
                        getCalculateProductsPrice={setCalculatedProducts}
                    />
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <Payments
                            control={control}
                            errors={errors}
                            tabValue={tabValue}
                            setTabValue={setTabValue}
                        />
                    </form>
                </Grid>
            </Grid>
            <div className="text-right">
                <BaseButton
                    className="variant-red btn-lg-width"
                    onClick={handlePayNow}
                    isLoading={disabled}
                >
                    Pay Now
                </BaseButton>
            </div>
            <ReviewAndConfirmModal
                open={showPayNowModal}
                onClose={() => setShowPayNowModal(false)}
            />
        </div>
    )
}

// store section
const mapStateToProps = (store: any) => {
    const { firearms, accessories, parts } = store?.client

    return {
        firearms: firearms?.firearmData,
        accessories: accessories?.accessoriesData,
        parts: parts?.partData,
    }
}

const mapDispatchToProps = (dispatch: Redux.Dispatch) => {
    const { getItems } = ClientFirearmActions
    const { createInvoice } = ClientMyAccountActions
    return {
        getFirearms: (params: any) =>
            dispatch(getItems('FIREARM', params) as any),
        getAccessories: (params: any) =>
            dispatch(getItems('ACCESSORY', params) as any),
        getParts: (params: any) => dispatch(getItems('PART', params) as any),
        createInvoice: (payload: any) =>
            dispatch(createInvoice(payload) as any),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(MyAccount)
